class TrackOrderForm extends HTMLElement {
    constructor() {
        super();
        this.form = null;
        this.contactInput = null;
        this.orderInput = null;
        this.trackOrderResult = null;
        this.backLink = null;
        this.abortController = null;
        
        // Configuration
        this.config = {
            apiEndpoint: '/apps/tra-cuu-don-hang',
            requestTimeout: 30000, // 30 seconds
            locale: this.getAttribute('locale') || 'vi-VN',
            currency: this.getAttribute('currency') || 'VND'
        };
        
        // Translation dictionary
        this.translations = this.getTranslations();
    }

    connectedCallback() {
        try {
            this.initializeElements();
            this.attachEventListeners();
        } catch (error) {
            console.error('Error initializing TrackOrderForm:', error);
            this.showError(this.t('initError'));
        }
    }

    disconnectedCallback() {
        // Cleanup: abort pending requests
        if (this.abortController) {
            this.abortController.abort();
        }
    }

    initializeElements() {
        this.form = this.querySelector('#track-order-form');
        this.contactInput = this.querySelector('#contact');
        this.orderInput = this.querySelector('#order-number');
        this.trackOrderResult = document.querySelector('track-order-results');
        this.backLink = document.querySelector('#back-to-form');

        if (!this.form) {
            throw new Error('Track order form not found');
        }
    }

    attachEventListeners() {
        this.form.addEventListener('submit', this.handleSubmit.bind(this));
        
        if (this.backLink) {
            this.backLink.addEventListener('click', this.handleBackClick.bind(this));
        }
    }

    handleBackClick(e) {
        e.preventDefault();
        this.removeAttribute('status');
        this.showTrackOrderForm();
        if (this.trackOrderResult) {
            this.trackOrderResult.setAttribute('status', 'loading');
        }
    }

    async handleSubmit(e) {
        e.preventDefault();

        const contact = this.contactInput.value.trim();
        const orderId = this.orderInput.value.trim();

        // Validation
        if (!this.validateInputs(contact, orderId)) {
            return;
        }

        const button = this.form.querySelector('.track-button');
        const originalText = button.textContent;
        
        this.setLoadingState(button, true);

        try {
            const data = await this.fetchOrderData(contact, orderId);
            this.renderResult(data);
            this.setAttribute('status', 'loaded');
            this.hideTrackOrderForm();
            if (this.trackOrderResult) {
                this.trackOrderResult.setAttribute('status', 'loaded');
            }
        } catch (error) {
            this.handleError(error);
        } finally {
            this.setLoadingState(button, false, originalText);
        }
    }

    validateInputs(contact, orderId) {
        if (!contact || !orderId) {
            this.showError(this.t('requiredFields'));
            return false;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(contact)) {
            this.showError(this.t('invalidEmail'));
            return false;
        }

        return true;
    }

    async fetchOrderData(email, orderId) {
        // Cancel previous request if exists
        if (this.abortController) {
            this.abortController.abort();
        }

        this.abortController = new AbortController();
        const timeoutId = setTimeout(() => this.abortController.abort(), this.config.requestTimeout);

        try {
            const response = await fetch(this.config.apiEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, orderId }),
                signal: this.abortController.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || this.t('fetchError'));
            }

            return await response.json();
        } catch (error) {
            clearTimeout(timeoutId);
            
            if (error.name === 'AbortError') {
                throw new Error(this.t('requestTimeout'));
            }
            throw error;
        }
    }

    setLoadingState(button, isLoading, originalText = '') {
        if (isLoading) {
            button.disabled = true;
            button.textContent = this.t('tracking');
            this.setAttribute('status', 'loading');
            if (this.trackOrderResult) {
                this.trackOrderResult.setAttribute('status', 'loading');
            }
        } else {
            button.disabled = false;
            button.textContent = originalText;
        }
    }

    handleError(error) {
        console.error('Order tracking error:', error);
        this.showError(error.message || this.t('generalError'));
        this.setAttribute('status', 'error');
    }

    showError(message) {
        alert(message);
    }

    maskMiddle(input) {
        if (!input) return '';
        const str = String(input).trim();
        const len = str.length;
        
        if (len <= 4) return str;

        const hiddenCount = Math.floor(len * 0.3);
        const start = 2;
        const end = start + hiddenCount;

        return str
            .split('')
            .map((ch, i) => (i >= start && i < end ? '*' : ch))
            .join('');
    }

    formatDate(isoDate) {
        if (!isoDate) return 'N/A';
        
        try {
            const date = new Date(isoDate);
            if (isNaN(date.getTime())) return 'N/A';
            
            return new Intl.DateTimeFormat(this.config.locale, {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            }).format(date);
        } catch (error) {
            console.error('Date formatting error:', error);
            return 'N/A';
        }
    }

    formatCurrency(amount, currencyCode = null) {
        if (!amount || isNaN(amount)) return 'N/A';
        
        const currency = currencyCode || this.config.currency;
        
        try {
            return new Intl.NumberFormat(this.config.locale, {
                style: 'currency',
                currency: currency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(parseFloat(amount));
        } catch (error) {
            console.error('Currency formatting error:', error);
            // Fallback to simple formatting
            return `${parseFloat(amount).toLocaleString(this.config.locale)} ${currency}`;
        }
    }

    renderResult(data) {
        try {
            this.renderOrderInfo(data);
            this.renderProgressSteps(data);
            this.renderShippingInfo(data);
            this.renderPaymentInfo(data);
            this.renderProductList(data);
        } catch (error) {
            console.error('Error rendering results:', error);
            this.showError(this.t('renderError'));
        }
    }

    renderOrderInfo(data) {
        const elements = {
            'order-number-result': this.orderInput.value.trim(),
            'order-status': this.getStatusLabel(data.orderStatus?.shipping_status?.at(-1)?.curent_status),
            'order-date': this.formatDate(data.createdAt),
            'estimated-delivery': this.formatDeliveryDate(data.orderStatus?.estimated_delivery_date)
        };

        Object.entries(elements).forEach(([id, value]) => {
            const el = document.getElementById(id);
            if (el) el.textContent = value || 'N/A';
        });
    }

    formatDeliveryDate(dateRange) {
        if (!dateRange) return 'N/A';
        return dateRange.replace(/-/g, ' - ');
    }

    getStatusLabel(status) {
        if (!status) return 'N/A';
        return this.t(`status_${status}`) || status.replace('_', ' ');
    }

    renderProgressSteps(data) {
        const allSteps = [
            { key: 'order_placed', label: this.t('status_order_placed') },
            { key: 'in_progress', label: this.t('status_in_progress') },
            { key: 'delay', label: this.t('status_delay') },
            { key: 'shipping', label: this.t('status_shipping') },
            { key: 'delivered', label: this.t('status_delivered') }
        ];

        const shippingStatus = data.orderStatus?.shipping_status || [];
        const availableStatuses = shippingStatus.map(s => s.curent_status);
        const steps = allSteps.filter(step => 
            availableStatuses.includes(step.key) || 
            step.key === 'shipping' || 
            step.key === 'delivered'
        );

        const progressContainer = document.getElementById('progress-steps');
        if (!progressContainer) return;

        const activeStatus = shippingStatus.at(-1)?.curent_status;
        const activeIndex = steps.findIndex(step => step.key === activeStatus);
        const progressPercent = activeIndex >= 0 ? ((activeIndex) / (steps.length - 1)) * 100 : 0;

        // Clear container
        progressContainer.innerHTML = '';

        // Render steps
        const stepsHTML = steps.map(step => {
            const record = shippingStatus.find(s => s.curent_status === step.key);
            const statusClass = record
                ? (step.key === activeStatus ? 'active' : 'completed')
                : 'pending';
            const date = record?.date || '&nbsp;';

            return `
                <div class="step ${step.key} status-${statusClass}">
                    <div class="step-icon ${statusClass}"></div>
                    <div class="step-label">${step.label}</div>
                    <div class="step-date">${date}</div>
                </div>
            `;
        }).join('');

        progressContainer.innerHTML = stepsHTML;

        // Render progress line
        requestAnimationFrame(() => {
            const firstStep = progressContainer.querySelector('.step');
            if (firstStep) {
                const stepWidth = firstStep.offsetWidth / 2;
                progressContainer.insertAdjacentHTML('afterbegin', `
                    <div class="progress-line">&nbsp;</div>
                    <div class="progress-line-active" style="width: calc(${progressPercent}% + ${stepWidth}px);">&nbsp;</div>
                `);
            }
        });
    }

    renderShippingInfo(data) {
        const addr = data.shippingAddress || {};
        const elements = {
            'customer-name': addr.name || 'N/A',
            'customer-phone': this.maskMiddle(addr.phone),
            'customer-address': this.maskMiddle(
                [addr.address1, addr.address2, addr.city, addr.country]
                    .filter(Boolean)
                    .join(', ')
            )
        };

        Object.entries(elements).forEach(([id, value]) => {
            const el = document.getElementById(id);
            if (el) el.textContent = value;
        });
    }

    renderPaymentInfo(data) {
        const totalPrice = this.formatCurrency(
            data.totalPriceSet?.presentmentMoney?.amount,
            data.totalPriceSet?.presentmentMoney?.currencyCode
        );

        const elements = {
            'order-value': totalPrice,
            'payment-method': data.paymentGatewayNames?.[0] || 'N/A',
            'tracking-number': this.orderInput.value.trim()
        };

        Object.entries(elements).forEach(([id, value]) => {
            const el = document.getElementById(id);
            if (el) el.textContent = value;
        });
    }

    renderProductList(data) {
        const productList = document.getElementById('product-list');
        if (!productList) return;

        const lineItems = data.lineItems || [];
        
        if (lineItems.length === 0) {
            productList.innerHTML = `<p>${this.t('noProducts')}</p>`;
            return;
        }

        const productsHTML = lineItems.map(item => {
            const total = this.formatCurrency(
                item.discountedTotalSet?.presentmentMoney?.amount,
                item.discountedTotalSet?.presentmentMoney?.currencyCode
            );
            const imageUrl = item.image?.url || '';
            const itemName = item.name || 'N/A';

            return `
                <div class="product-row">
                    <div class="product-info">
                        <img src="${imageUrl}" alt="${itemName}" class="product-image" loading="lazy">
                        <div class="product-details">
                            <h3>${itemName}</h3>
                            <div class="product-price">${total}</div>
                        </div>
                    </div>
                    <div class="quantity">${item.quantity || 0}</div>
                    <div class="total-price">${total}</div>
                </div>
            `;
        }).join('');

        productList.innerHTML = productsHTML;
    }

    hideTrackOrderForm() {
        setTimeout(() => {
            this.classList.add('hidden');
        }, 400);
    }

    showTrackOrderForm() {
        this.classList.remove('hidden');
    }

    // Translation helper
    t(key) {
        return this.translations[key] || key;
    }

    getTranslations() {
        const locale = this.config.locale;
        
        const translations = {
            'vi-VN': {
                requiredFields: 'Vui lòng nhập đầy đủ thông tin',
                invalidEmail: 'Email không hợp lệ',
                fetchError: 'Không thể tra cứu đơn hàng',
                requestTimeout: 'Yêu cầu hết thời gian chờ',
                generalError: 'Đã xảy ra lỗi khi tra cứu đơn hàng',
                initError: 'Lỗi khởi tạo form tra cứu',
                renderError: 'Lỗi hiển thị kết quả',
                tracking: 'Đang tra cứu...',
                noProducts: 'Không có sản phẩm',
                status_order_placed: 'Đã đặt hàng',
                status_in_progress: 'Đang xử lý',
                status_delay: 'Bị trễ',
                status_shipping: 'Đang giao',
                status_delivered: 'Đã giao'
            },
            'en-US': {
                requiredFields: 'Please fill in all fields',
                invalidEmail: 'Invalid email address',
                fetchError: 'Unable to track order',
                requestTimeout: 'Request timeout',
                generalError: 'An error occurred while tracking order',
                initError: 'Form initialization error',
                renderError: 'Error displaying results',
                tracking: 'Tracking...',
                noProducts: 'No products',
                status_order_placed: 'Order placed',
                status_in_progress: 'In progress',
                status_delay: 'Delayed',
                status_shipping: 'Shipping',
                status_delivered: 'Delivered'
            }
        };

        return translations[locale] || translations['en-US'];
    }
}

customElements.define('track-order-form', TrackOrderForm);